import React from "react";
import {
  CREW_NUDGE_MIN_DISTINCT,
  EVENT_SIGNAL_MIN_APPRECIATIONS,
  FD_RESULTS_DONE,
  fdResultsCommunityBlock,
  fdResultsProgressBlock,
  fdResultsSentBlock,
  fdResultsSurpriseBlock,
} from "../copy.js";
import { ReadableNavButton } from "../../../components/Sticker";
import DemoStickyBar from "../../../components/demo/DemoStickyBar";
import { DEMO_PAGE_PB_STICKY } from "../../../lib/demoRoutes";

const DONE_BTN_CLASS = "w-full min-h-[44px] py-3.5 text-[15px] font-ui font-medium";

function ResultsBlock({
  title,
  subtitle,
  content,
  testId,
  subtitleTestId,
  showDivider,
  centered = false,
  subtitleEmphasis = true,
}) {
  const subtitleClass = subtitleEmphasis
    ? "font-ui text-[15px] font-semibold text-ink leading-snug text-pretty"
    : "font-ui text-[15px] font-normal text-ink/80 leading-snug text-pretty";

  return (
    <div className={centered ? "text-center" : "text-left"}>
      {showDivider ? <hr className="border-0 border-t border-black/[0.08] mb-6" aria-hidden /> : null}
      <section className={`space-y-1.5 ${centered ? "mx-auto max-w-[20rem]" : ""}`} data-testid={testId}>
        <h2
          className={`font-ui font-semibold text-[18px] leading-snug text-ink text-pretty ${centered ? "text-balance" : ""}`}
          data-testid={testId === "favorite-dances-results-sent" ? "favorite-dances-results-title" : undefined}
        >
          {title}
        </h2>
        {subtitle ? (
          <p className={subtitleClass} data-testid={subtitleTestId}>
            {subtitle}
          </p>
        ) : null}
        {content ? (
          <p className="font-ui text-[15px] font-normal text-ink/80 leading-snug text-pretty">{content}</p>
        ) : null}
      </section>
    </div>
  );
}

export default function ResultsStep({
  myAppreciationsReceived = 0,
  myPickCount = 0,
  eventTotalPicks = 0,
  eventSignalMinAppreciations = EVENT_SIGNAL_MIN_APPRECIATIONS,
  crewDistinctCount = 0,
  crewNudgeMinDistinct = CREW_NUDGE_MIN_DISTINCT,
  onDone,
  footer = true,
  stickyFooter = false,
  doneTestId = "favorite-dances-results-done",
  doneLabel = FD_RESULTS_DONE,
}) {
  const surprise = fdResultsSurpriseBlock(myAppreciationsReceived);
  const community = fdResultsCommunityBlock(eventTotalPicks, eventSignalMinAppreciations);

  const blocks = [
    {
      ...fdResultsSentBlock(myPickCount),
      testId: "favorite-dances-results-sent",
      subtitleTestId: "favorite-dances-picks-sent",
      centered: true,
      subtitleEmphasis: false,
    },
    surprise && { ...surprise, testId: "favorite-dances-personal" },
    community && { ...community, testId: "favorite-dances-community" },
    { ...fdResultsProgressBlock(crewDistinctCount, crewNudgeMinDistinct), testId: "favorite-dances-feed-progress" },
  ].filter(Boolean);

  const showInlineFooter = footer && !stickyFooter;
  const showStickyFooter = stickyFooter && footer;

  const doneButton = (
    <ReadableNavButton
      type="button"
      variant="night"
      className={showStickyFooter ? `flex-1 ${DONE_BTN_CLASS}` : DONE_BTN_CLASS}
      onClick={onDone}
      data-testid={doneTestId}
    >
      {doneLabel}
    </ReadableNavButton>
  );

  const content = (
    <div
      className={`space-y-6 ${showStickyFooter ? DEMO_PAGE_PB_STICKY : ""} ${showInlineFooter ? "py-2" : "py-4"}`}
      data-testid="favorite-dances-results"
      role="status"
      aria-live="polite"
    >
      {blocks.map((block, index) => (
        <ResultsBlock
          key={block.testId}
          title={block.title}
          subtitle={block.subtitle}
          content={block.content}
          testId={block.testId}
          subtitleTestId={block.subtitleTestId}
          showDivider={index > 0}
          centered={block.centered}
          subtitleEmphasis={block.subtitleEmphasis !== false}
        />
      ))}

      {showInlineFooter && <div className="pt-1">{doneButton}</div>}
    </div>
  );

  if (!showStickyFooter) return content;

  return (
    <>
      {content}
      <DemoStickyBar testId="favorite-dances-results-sticky">{doneButton}</DemoStickyBar>
    </>
  );
}
